import { Controller } from "stimulus";

const clock = (secs: number): string => {
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export default class extends Controller {
  private audio?: HTMLAudioElement;
  private button?: HTMLElement;
  private player?: HTMLElement;
  private total = 0;
  private unbind?: () => void;

  disconnect(): void {
    this.stop();
  }

  play(event: Event): void {
    const button = event.currentTarget as HTMLElement;
    const item = button.closest(".tl-item");
    const player = item?.querySelector<HTMLElement>("[data-call-recording-target=player]");
    if (!item || !player || !button.dataset.src) return;

    if (this.button === button && this.audio) {
      if (this.audio.paused) this.audio.play().catch(() => undefined);
      else this.audio.pause();
      return;
    }

    this.stop();
    this.button = button;
    this.player = player;
    this.total = Number(button.dataset.duration) || 0;
    item.querySelector<HTMLElement>("[data-error]")?.setAttribute("hidden", "");
    player.hidden = false;

    const audio = new Audio(button.dataset.src);
    this.audio = audio;
    this.setLoading(true);
    audio.addEventListener("waiting", () => this.setLoading(true));
    audio.addEventListener("playing", () => this.setLoading(false));
    audio.addEventListener("play", () => this.setPlaying(true));
    audio.addEventListener("pause", () => this.setPlaying(false));
    audio.addEventListener("ended", () => this.setPlaying(false));
    audio.addEventListener("timeupdate", () => this.render());
    audio.addEventListener("error", () => {
      const error = item.querySelector<HTMLElement>("[data-error]");
      this.stop();
      error?.removeAttribute("hidden");
    });

    const bar = player.querySelector<HTMLElement>("[data-seek]");
    const onClick = (e: MouseEvent) => {
      if (bar) this.seekTo(((e.clientX - bar.getBoundingClientRect().left) / bar.clientWidth) * this.length());
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      this.seekTo(audio.currentTime + (e.key === "ArrowRight" ? 5 : -5));
    };
    bar?.addEventListener("click", onClick);
    bar?.addEventListener("keydown", onKey);
    this.unbind = () => {
      bar?.removeEventListener("click", onClick);
      bar?.removeEventListener("keydown", onKey);
    };

    audio.play().catch(() => undefined);
  }

  private length(): number {
    const d = this.audio?.duration;
    return d && Number.isFinite(d) ? d : this.total;
  }

  private seekTo(secs: number): void {
    if (!this.audio || !this.length()) return;
    this.audio.currentTime = Math.min(Math.max(secs, 0), this.length());
    this.render();
  }

  private render(): void {
    if (!this.audio || !this.player) return;
    const length = this.length();
    const now = this.audio.currentTime;
    const current = this.player.querySelector<HTMLElement>("[data-current]");
    const fill = this.player.querySelector<HTMLElement>("[data-fill]");
    const bar = this.player.querySelector<HTMLElement>("[data-seek]");
    if (current) current.textContent = clock(now);
    if (fill) fill.style.width = length ? `${Math.min(100, (now / length) * 100)}%` : "0";
    bar?.setAttribute("aria-valuenow", String(Math.floor(now)));
  }

  private setLoading(on: boolean): void {
    this.button?.classList.toggle("is-loading", on);
    this.player?.classList.toggle("is-loading", on);
  }

  private setPlaying(on: boolean): void {
    if (!this.button) return;
    this.button.classList.toggle("is-playing", on);
    if (!on) this.setLoading(false);
    const label = on ? "Pause recording" : "Play recording";
    this.button.setAttribute("aria-label", label);
    this.button.title = label;
  }

  private stop(): void {
    this.unbind?.();
    this.unbind = undefined;
    this.audio?.pause();
    this.audio?.removeAttribute("src");
    this.setPlaying(false);
    this.button?.classList.remove("is-loading");
    if (this.player) {
      this.player.hidden = true;
      this.player.classList.remove("is-loading");
      const fill = this.player.querySelector<HTMLElement>("[data-fill]");
      const current = this.player.querySelector<HTMLElement>("[data-current]");
      if (fill) fill.style.width = "0";
      if (current) current.textContent = "0:00";
    }
    this.audio = undefined;
    this.button = undefined;
    this.player = undefined;
  }
}
